import { Container, Sprite, type Texture } from "pixi.js";
import type { PresentationTaskRunner } from "@slot/presentation";
import { METER_FLAMES_TUNING } from "../tuning/featureTuning";
import { METER_FLAMES_LAYOUT, art } from "./layout";

/** The flames' art, as the art import writes it (`game-deferred/meter-flames`). */
export interface MeterFlamesArt {
  /** The fire's flipbook, in order: each its whole image. */
  readonly frames: readonly Texture[];
  readonly glow: Texture | null;
  readonly spark: Texture | null;
}

interface Spark {
  node: Sprite;
  /** Where it started and how far it is in its life (artboard pixels from the artboard's centre; seconds). */
  x: number;
  y: number;
  age: number;
  life: number;
  rise: number;
  phase: number;
  scale: number;
}

const { artboard, sparks: SPARKS } = METER_FLAMES_LAYOUT;

/**
 * The respin meter's flames while LIFE is in play (the artist's spin counter): a green fire burning round the meter's
 * plate — the artist's flipbook, played at its rate — over a soft glow, sparks rising from its top edge. Three layers the
 * meter puts in place: the glow under its frame, the fire over the frame (its middle open on the slots), the sparks over
 * all, each at the artboard's centre. The art arrives after the game starts (`setArt`): until then nothing shows. Its time
 * is the UI's: it never speeds up with the round, and it is no work a round waits for.
 */
export class MeterFlamesView {
  public readonly back = new Container({ label: "MeterFlamesGlow" });
  public readonly front = new Container({ label: "MeterFlames" });
  public readonly top = new Container({ label: "MeterFlamesSparks" });
  private frames: readonly Texture[] = [];
  private fire: Sprite | null = null;
  private sparkArt: Texture | null = null;
  private readonly sparks: Spark[] = [];
  private on = false;
  /** How far the flames have come in, 0..1. */
  private level = 0;
  private clock = 0;
  private untilSpark = 0;
  private ticking = false;
  private disposed = false;

  public constructor(private readonly input: { runner: PresentationTaskRunner; random?: () => number }) {
    const plate = { x: art(METER_FLAMES_LAYOUT.plate.x), y: art(METER_FLAMES_LAYOUT.plate.y) };
    for (const layer of this.layers) {
      layer.position.set(plate.x, plate.y + art(METER_FLAMES_LAYOUT.artboardY));
      layer.alpha = 0;
      layer.visible = false;
    }
  }

  /** The flames' art, once loaded: they burn from now on whenever LIFE is in play. */
  public setArt(flames: MeterFlamesArt): void {
    if (this.disposed || flames.frames.length === 0) return;
    this.frames = flames.frames;
    if (flames.glow) this.back.addChild(cover(new Sprite({ texture: flames.glow, anchor: 0.5, label: "Glow" })));
    this.fire = cover(new Sprite({ texture: this.frames[0], anchor: 0.5, label: "Fire" }));
    this.front.addChild(this.fire);
    this.sparkArt = flames.spark;
    if (this.on) this.start();
  }

  /** LIFE is in play (`true`): the flames come in; its refill spent or the bonus over, they go. */
  public setActive(active: boolean): void {
    if (active === this.on) return;
    this.on = active;
    if (active) this.untilSpark = 0;
    this.start();
  }

  /** Whether the flames show now (the dev hook's read): LIFE in play, their art loaded, come in. */
  public get burning(): boolean {
    return this.on && this.fire !== null && this.level > 0;
  }

  public dispose(): void {
    this.disposed = true;
  }

  private get layers(): Container[] {
    return [this.back, this.front, this.top];
  }

  private start(): void {
    if (this.ticking || this.fire === null || this.disposed) return;
    this.ticking = true;
    let last = 0;
    void this.input.runner
      .tickInScope(
        "",
        (elapsed) => {
          this.tick(elapsed - last);
          last = elapsed;
        },
        () => this.disposed || (!this.on && this.level === 0 && this.sparks.length === 0),
      )
      .then(() => {
        this.ticking = false;
        // A wait that ended as it began again keeps burning.
        if (this.on) this.start();
      });
  }

  private tick(dt: number): void {
    const t = METER_FLAMES_TUNING;
    const step = dt / Math.max(1e-6, t.fadeSeconds);
    this.level = this.on ? Math.min(1, this.level + step) : Math.max(0, this.level - step);
    for (const layer of this.layers) {
      layer.alpha = this.level;
      layer.visible = this.level > 0;
    }
    this.clock += dt;
    if (this.fire && this.frames.length > 0) this.fire.texture = this.frames[Math.floor(this.clock * t.fps) % this.frames.length]!;
    if (this.on && this.sparkArt) {
      this.untilSpark -= dt;
      while (this.untilSpark <= 0) {
        this.spawnSpark(this.sparkArt);
        this.untilSpark += between(t.sparkEverySeconds, this.random());
      }
    }
    for (let i = this.sparks.length - 1; i >= 0; i--) {
      const spark = this.sparks[i]!;
      spark.age += dt;
      const k = spark.age / spark.life;
      if (k >= 1 || spark.node.destroyed) {
        spark.node.destroy();
        this.sparks.splice(i, 1);
        continue;
      }
      const x = spark.x + t.sparkSway * Math.sin(spark.phase + spark.age * 2.2);
      const y = spark.y - spark.rise * spark.age;
      spark.node.position.set(art(x), art(y));
      // In quickly, out over its last half.
      spark.node.alpha = Math.min(1, k / 0.12) * Math.min(1, (1 - k) / 0.5);
      const s = spark.scale * (1 - 0.35 * k);
      spark.node.scale.set(s, s);
    }
  }

  private spawnSpark(texture: Texture): void {
    const t = METER_FLAMES_TUNING;
    const node = new Sprite({ texture, anchor: 0.5, label: "Spark" });
    node.blendMode = "add";
    node.setSize(art(SPARKS.size), art(SPARKS.size));
    const { left, right, top, bottom } = SPARKS.spawn;
    this.sparks.push({
      node,
      x: left + (right - left) * this.random() - artboard.width / 2,
      y: top + (bottom - top) * this.random() - artboard.height / 2,
      age: 0,
      life: between(t.sparkSeconds, this.random()),
      rise: between(t.sparkRise, this.random()),
      phase: this.random() * Math.PI * 2,
      scale: node.scale.x * between(t.sparkScale, this.random()),
    });
    node.alpha = 0;
    this.top.addChild(node);
  }

  private random(): number {
    return (this.input.random ?? Math.random)();
  }
}

/** An artboard-sized sprite: the whole artboard, in design units. */
function cover(sprite: Sprite): Sprite {
  sprite.setSize(art(artboard.width), art(artboard.height));
  return sprite;
}

function between([low, high]: readonly [number, number] | readonly number[], k: number): number {
  return low! + (high! - low!) * k;
}
